import * as React from 'react';
import { makeStyles, tokens, Spinner, Avatar, Button, Tooltip, Switch } from '@fluentui/react-components';
import { COPILOT_CONSTANTS } from './constants';
import { UserMessageV2 as UserMessage, CopilotMessageV2 as CopilotMessage, AiGeneratedDisclaimer } from '@fluentui-copilot/react-copilot-chat';
import { CopyRegular, CheckmarkRegular } from '@fluentui/react-icons';
import { ServiceContext } from '@msx/platform-services';
import CopilotMessageReference from './CopilotMessageReference';
import CustomUserMessage from './CustomUserMessage';
import GraphMessage from './MessageTypes/GraphMessage';
import MarkdownMessage from './MessageTypes/MarkdownMessage';
import AdaptiveCardMessage from './MessageTypes/AdaptiveCardMessage';
import FormMessage from './MessageTypes/FormMessage';
import TicketTable from './MessageTypes/TicketTable';
import ErrorMessage from './MessageTypes/ErrorMessage';
import CopilotErrorBoundary from './CopilotErrorBoundary';
import { MCAPSCopilotTelemetry } from './utils/mcapsCopilotTelemetry';
import { isAdaptiveCardMessage, extractAdaptiveCardJson, getAdaptiveCardFallbackText } from './utils/adaptiveCardDetection';
import { appConfig } from './../../../app/App.config';


interface FormField {
  id: string;
  type: 'text' | 'textarea' | 'dropdown' | 'info' | 'peoplePicker' | 'file';
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  maxLength?: number;
  validation?: {
    pattern?: string;
    maxLength?: number;
    errorMessage?: string;
  };
  apiConfig?: {
    endpoint: string;
    queryParams?: Record<string, string>;
  };
  onChange?: {
    updateFields?: string[];
  };
  nextSection?: {
    section: string;
    showWhen: Record<string, any>;
  };
  text?: string; // for info type fields
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'error' | 'separator';
  content: string;
  timestamp?: number;
  source?: string;
  intent?: string;  
  showNewConversation?: boolean;
  separator?: string;
  from?: string;
  showSignInButton?: boolean;
  meta?: {
    sources?: Array<{ title?: string; url?: string; content?: string; score?: number }>;
    ticketGuidance?: string;
    confidence?: number;
    processingTime?: number;
    tableHtml?: string;
    tableNote?: string;
    // Form-related properties
    formFields?: FormField[];
    formTitle?: string;
    formDescription?: string;
    // Error-related properties
    originalError?: Error | string;
    //Adaptive Card related properties
    adaptiveCard?: any;
  };
}

interface MessageBodyProps {
  messages: Message[];
  isLoading?: boolean;
  onFormSubmit?: (formData: { values: Record<string, any>; isValid: boolean; errors: Record<string, string> }) => void;
  onShowNextSection?: (data: { sectionName: string; triggerField: string; triggerValue: any }) => void;
  userEmail?: string;
  apiUrl?: string;
  persistentFormValues?: Record<string, any>;
  onPromptSelected?: (prompt: string) => void; 
  onExitToggle?: (checked?: boolean) => void;
  shouldDisableAllSwitches?: boolean; // Disable all exit switches based on latest API response
  switchDisableState?: {
    disableAllSwitches: boolean;
    latestMessageId: string | null;
    latestFormSessionActive: boolean;
  }; // Enhanced switch disable state for granular control
  mcapsTelemetry?: MCAPSCopilotTelemetry; // Add telemetry instance
  onRemoveMessage?: (messageId: string) => void; // Callback to remove message
  onAdaptiveCardAction?: (actionData: any, messageId: string) => void; // Callback for Adaptive Card Action.Submit
}

const useStyles = makeStyles({
  messageContainer: {
    // No scrolling or padding - parent handles it
    width: '100%',
    maxWidth: '100%',
    minWidth: 0, // Allow shrinking
    overflow: 'hidden', // Prevent expansion beyond container
    '& > div': {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0
    }
  },
  answerBox: {
    background: '#fff',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '12px 16px !important',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    marginBottom: '8px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box', // Include padding in width calculation
    overflowWrap: 'break-word' // Break long text
  },
  welcomeBox: {
    background: '#fff',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    marginBottom: '8px',
    textAlign: 'center' as const,
    fontSize: '16px',
    color: '#323130'
  },
  userMessageBox: {
    padding: '2px 0', // reduced vertical padding
    marginBottom: '4px' // tighter gap below question
  },  
  htmlTableNote: {
    marginTop: '6px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3
  },
  systemText: { 
    fontStyle: 'italic' 
  },
  timestampRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '4px'
  },
  timestampText: {
    fontSize: '11px',
    opacity: 0.55,
    marginLeft: '8px'
  },
  metaInfoLine: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    fontSize: '11px',
    marginTop: '6px',
    color: tokens.colorNeutralForeground2
  },
  copyButton: {
    minWidth: 'auto',
    padding: '4px 8px',
    height: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: 'transparent',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorNeutralStroke1Hover}`
    }
  },
  singleMessageContainer: { 
    
    display: 'contents'
  },
  topMetaBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px'
  },
  draftSwitchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: '400',
    color: tokens.colorNeutralForeground2
  },
  messageScrollArea: {
    width: '100%',
    maxWidth: '100%',
    
    overflowWrap: 'break-word',
    wordBreak: 'break-word' 
  },
  loadingWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '12px 0'
  },
  
  separatorContainer: {
    display: "inline-flex" as const,
    width: "100%",
    color: "rgb(170, 170, 170)",
    alignItems: "center",
    margin: "16px 0"
  },
  separatorLine: {
    borderTop: "1px solid rgb(170, 170, 170)",
    margin: "10px",
    width: "100%"
  },
  sessionExpiredBox: {
    padding: '16px',
    backgroundColor: '#fff4e5',
    border: '1px solid #ffb900',
    borderRadius: '8px',
    marginBottom: '12px',
    textAlign: 'center' as const
  },
  sessionExpiredText: {
    marginBottom: '12px',
    color: '#323130',
    fontSize: '14px'
  },
  signInButton: {
    padding: '8px 24px',
    backgroundColor: '#0078d4',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    ':hover': {
      backgroundColor: '#106ebe'
    },
    ':active': {
      backgroundColor: '#005a9e'
    }
  }
});

const MessageBody: React.FC<MessageBodyProps> = ({ 
  messages, 
  isLoading,
  onFormSubmit,
  onShowNextSection,
  userEmail = '',
  apiUrl = '',
  persistentFormValues = {},
  onPromptSelected,
  onExitToggle,
  shouldDisableAllSwitches = false,
  switchDisableState,
  mcapsTelemetry,
  onRemoveMessage,
  onAdaptiveCardAction,
}) => {
  const styles = useStyles();
  const platformContext = React.useContext(ServiceContext);
  const authContext = (platformContext as any)?.authContext;
  
  // Read feature flag for Adaptive Card rendering
  const enableAdaptiveCardRendering = appConfig.copilotConfig?.enableAdaptiveCardRendering || false;
  
  const formatTimestamp = (timestamp?: number) => {
    return timestamp ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  };

  // Enhanced function to determine if a specific switch should be disabled
  const shouldDisableSwitch = (messageId: string): boolean => {
    // Condition 1: If latest response has formSessionActive: false, disable ALL switches
    if (shouldDisableAllSwitches || switchDisableState?.disableAllSwitches) {
      return true;
    }
    
    // Condition 2: If latest response has formSessionActive: true, disable all previous switches
    // except the latest one (keep only the latest switch active)
    if (switchDisableState?.latestFormSessionActive && switchDisableState.latestMessageId) {
      return messageId !== switchDisableState.latestMessageId;
    }
    
    return false;
  };

  const handleExitToggle = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    // Log telemetry for switch interaction
    if (mcapsTelemetry) {
      mcapsTelemetry.logExitSwitchToggle({
        action: "click",
        message: `Exit switch ${data.checked ? 'enabled' : 'disabled'} by user`
      });
    }
    
    if (onExitToggle) {
      onExitToggle(data.checked);
    }
  };

  const [copiedMessageId, setCopiedMessageId] = React.useState<string | null>(null);

  // Clipboard write with fallback for iframes and non-HTTPS contexts
  const writeToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Fall through to fallback
    }
    // Fallback: use a hidden textarea + execCommand('copy')
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch {
      return false;
    }
  };

  // Resolve the actual displayed content for a message (matches rendering logic)
  const getDisplayedContent = (message: Message): string => {
    const responseType = (message.meta as any)?.responseType as string | undefined;
    const dataToDisplay = (message.meta as any)?.dataToDisplay as any;
    const ticketGuidance = (message.meta as any)?.ticketGuidance as string | undefined;

    // For error messages, use message.content directly
    if (responseType === 'error' || message.role === 'error') {
      return message.content || '';
    }

    // For table responses, build a text representation
    if (responseType === 'table' && dataToDisplay?.columns && dataToDisplay?.rows) {
      const header = dataToDisplay.columns.join('\t');
      const rows = (dataToDisplay.rows as any[]).map((row: any) =>
        dataToDisplay.columns.map((col: string) => row[col] ?? '').join('\t')
      ).join('\n');
      return dataToDisplay.responseText
        ? `${dataToDisplay.responseText}\n\n${header}\n${rows}`
        : `${header}\n${rows}`;
    }

    // For text / markdown / graph responses, use the same fallback chain as rendering
    const markdownText = (
      (dataToDisplay?.markdownString as string | undefined) ??
      ((message as any)?.meta?.markDownResult as string | undefined) ??
      message.content
    ) as string;

    const baseText = typeof markdownText === 'string' ? markdownText : String(markdownText ?? '');
    if (!ticketGuidance || typeof ticketGuidance !== 'string') {
      return baseText;
    }

    const normalizeLineBreaks = (value: string): string =>
      value.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\r\n/g, '\n').trim();

    const normalizedBaseText = normalizeLineBreaks(baseText);
    const normalizedGuidance = normalizeLineBreaks(ticketGuidance);
    const guidanceLeadLine = normalizedGuidance.split('\n').find((line) => line.trim().length > 0)?.trim() || '';
    const alreadyIncluded =
      (guidanceLeadLine && normalizedBaseText.includes(guidanceLeadLine)) ||
      normalizedBaseText.includes(normalizedGuidance);

    if (alreadyIncluded) {
      return baseText;
    }

    return [baseText, normalizedGuidance].filter(Boolean).join('\n\n');
  };

  const handleCopyMessage = async (message: Message) => {
    const content = getDisplayedContent(message);
    const success = await writeToClipboard(content);

    if (success) {
      // Visual feedback
      setCopiedMessageId(message.id);
      setTimeout(() => setCopiedMessageId(null), 2000);

      // Telemetry
      if (mcapsTelemetry) {
        mcapsTelemetry.logContentCopied({ action: 'click', message: 'User copied content from MCAPSHelp Copilot' });
      }

      // Accessibility announcement
      const ariaLiveRegion = document.getElementById('global-aria-live');
      if (ariaLiveRegion) {
        ariaLiveRegion.textContent = 'Message copied to clipboard';
        setTimeout(() => { ariaLiveRegion.textContent = ''; }, 3000);
      }
    } else {
      console.error('Failed to copy text to clipboard');
      const ariaLiveRegion = document.getElementById('global-aria-live');
      if (ariaLiveRegion) {
        ariaLiveRegion.textContent = 'Failed to copy message';
        setTimeout(() => { ariaLiveRegion.textContent = ''; }, 3000);
      }
    }
  };

  // Basic inline markdown for **bold** and *italic*
  const renderInline = (text: string) => {
    const boldSplit = text.split(/(\*\*[^*]+\*\*)/g).flatMap(segment => {
      if (/^\*\*[^*]+\*\*$/.test(segment)) {
        return <strong key={Math.random()}>{segment.slice(2, -2)}</strong>;
      }
      return segment.split(/(\*[^*]+\*)/g).map(s => 
        /^\*[^*]+\*$/.test(s) ? <em key={Math.random()}>{s.slice(1, -1)}</em> : s
      );
    });
    return boldSplit;
  };

  return (
    <>
      {messages.map(message => {
        const tsLabel = formatTimestamp(message.timestamp);        
        if (message.role === 'user') {
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              tabIndex={0}
              style={{ display: "flex", justifyContent: "end" }}
            >
              <CustomUserMessage
                message={message}
                onPromptSelected={onPromptSelected}
              >
                <div className={styles.userMessageBox}>
                  <span style={{marginRight:"5px"}}>{renderInline(message.content)}</span>
                </div>
              </CustomUserMessage>
            </div>
          );
        }

        
        if (message.role === 'separator' && message.showSignInButton) {
          return (
            <div className={styles.sessionExpiredBox} key={message.id} role="alert">
              <div className={styles.sessionExpiredText}>
                <strong>Session Timeout</strong>
                <br />
                {message.content}
              </div>
            </div>
          );
        }
        
        if (message.role === 'separator' && message.showNewConversation) {
          return (
            <div className={styles.separatorContainer} key={message.id}>
              <div className={styles.separatorLine}></div>
              <div style={{ minWidth: 120, textAlign: "center" }}>
                <i>{message.content || COPILOT_CONSTANTS.MESSAGES.NEW_CONVERSATION}</i>
              </div>
              <div className={styles.separatorLine}></div>
            </div>
          );
        }

        
        const isWelcomeMessage = message.id === 'welcome' || message.id === 'welcome-refresh';
        const boxClass = isWelcomeMessage ? styles.welcomeBox : styles.answerBox;

        const name = message.role === 'assistant' ? '' : message.role === 'error' ? 'Error' : 'System';
        const avatar = message.role === 'assistant' || message.role === 'error'
          ? null // No avatar for assistant and error messages
          : <Avatar size={24} color="brand" />;

        
        const responseType = (message.meta as any)?.responseType as string | undefined;
        const dataToDisplay = (message.meta as any)?.dataToDisplay as any;
        const formSessionActive = (message.meta as any)?.formSessionActive || dataToDisplay?.formSessionActive || false;
        const isFormMessage = (responseType === 'form' || responseType === 'json') && message.meta?.formFields && message.meta.formFields.length > 0;

        return (          
          <CopilotMessage 
            className={`responsebox ${boxClass}`} 
            key={message.id}
            data-message-id={message.id}
            name={name} 
            avatar={avatar} 
            style={{ width: '100%' }} 
          >
            <div className={styles.messageScrollArea}>
                {message.role === 'system' && (
                  <CopilotErrorBoundary>
                    <span className={styles.systemText}>{renderInline(message.content)}</span>
                  </CopilotErrorBoundary>
                )}
                {(message.role === 'assistant' || message.role === 'error') && (() => {                  
                  // ADAPTIVE CARD RENDERING (Feature Flag Controlled)
                  // Priority: Check for Adaptive Card first when feature flag is enabled
                  if (enableAdaptiveCardRendering && isAdaptiveCardMessage(message)) {
                    const cardJson = extractAdaptiveCardJson(message);
                    const fallbackText = getAdaptiveCardFallbackText(message);
                    
                    if (cardJson) {
                      return (
                        <CopilotErrorBoundary>
                          <AdaptiveCardMessage
                            cardJson={cardJson}
                            onAction={(actionData) => {
                              if (onAdaptiveCardAction) {
                                onAdaptiveCardAction(actionData, message.id);
                              }
                            }}
                            fallbackText={fallbackText}
                          />
                        </CopilotErrorBoundary>
                      );
                    }
                  }
                                                      
                  if (responseType === 'error' || message.role === 'error') {
                    return (
                      <CopilotErrorBoundary>
                        <ErrorMessage 
                          message={message.content}
                          originalError={message.meta?.originalError}
                          onCopy={() => handleCopyMessage(message)}
                        />
                      </CopilotErrorBoundary>
                    );
                  }
                  // (responseType === 'form' || responseType === 'json') &&
                  
                  
                  if ((responseType === 'form' || responseType === 'json') && message.meta?.formFields && message.meta.formFields.length > 0) {
                    return (
                      <CopilotErrorBoundary>
                        <FormMessage
                          fields={message.meta.formFields}
                          formTitle={message.meta.formTitle}
                          formDescription={message.meta.formDescription}
                          apiUrl={apiUrl}
                          userEmail={userEmail}
                          apiJsonResponse={dataToDisplay || (message.meta as any)?.apiResponse || message.meta}
                          onFormSubmit={onFormSubmit}
                          onShowNextSection={onShowNextSection}
                          persistentFormValues={persistentFormValues}
                          isActiveForm={!shouldDisableSwitch(message.id)}
                        />
                      </CopilotErrorBoundary>
                    );
                  }                  
                    let mainContent;
                    let markdownText = (
                      (dataToDisplay?.markdownString as string | undefined) ??
                      ((message as any)?.meta?.markDownResult as string | undefined) ??
                      message.content
                    ) as string;
                    
                    // Clean up the markdown text
                    if (typeof markdownText === 'string') {
                      // Remove surrounding quotes if they exist
                      const trimmed = markdownText.trim();
                      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
                          (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
                        markdownText = trimmed.slice(1, -1);
                      }                     
                      
                      markdownText = markdownText
                        .replace(/\\r\\n/g, '\n')  
                        .replace(/\\n/g, '\n')     
                        .replace(/\n\r\n/g, '\n\n') 
                        .replace(/\r\n/g, '\n')    
                        .trim();
                    }

                    if (responseType === 'graph' || responseType === 'bar_chart') {                      
                      mainContent = (
                        <CopilotErrorBoundary>
                          <MarkdownMessage
                            markdown={markdownText}
                            ticketGuidance={(message.meta as any)?.ticketGuidance}
                          />
                          <div style={{ marginTop: 12 }}>
                            <GraphMessage 
                              plotData={dataToDisplay?.plotData} 
                              layout={dataToDisplay?.layout}
                              structuredData={dataToDisplay?.structuredData || (message.meta as any)?.structuredData}
                            />
                          </div>
                        </CopilotErrorBoundary>
                      );
                    } else if (responseType === 'table') {
                      // Handle table format with structured data
                      mainContent = (
                        <CopilotErrorBoundary>
                          <TicketTable
                            columns={dataToDisplay?.columns}
                            rows={dataToDisplay?.rows}
                            responseText={dataToDisplay?.responseText || markdownText}
                            suppressTextContent={dataToDisplay?.suppressTextContent}
                            tableHtml={dataToDisplay?.tableHtml}
                            note={dataToDisplay?.note}
                          />
                        </CopilotErrorBoundary>
                      );
                    } else if (responseType === 'text' || responseType === 'markdown') {                      
                      mainContent = (
                        <CopilotErrorBoundary>
                          <MarkdownMessage
                            markdown={markdownText}
                            ticketGuidance={(message.meta as any)?.ticketGuidance}
                          />
                        </CopilotErrorBoundary>
                      );
                    }                  
                  
                  return (
                    <>
                      {mainContent}
                      {message.meta?.sources && message.meta.sources?.length > 0 && (
                        <CopilotMessageReference messageId={message.id} sources={message.meta.sources} />
                      )}
                    </>
                  );
                })()}
                {/* Move meta info outside IIFE so it shows for all response types */}
                {process.env.NODE_ENV === 'development' && (message.role === 'assistant' || message.role === 'error') && (message.meta?.confidence || message.meta?.processingTime) && (                 
                  <div className={styles.metaInfoLine}>
                    {message.meta?.confidence && <span>Confidence: {Math.round(message.meta.confidence * 100)}%</span>}
                    {/* {message.meta?.sources && <span>Documents: {Math.min(message.meta.sources.length, 5)}</span>} */}
                    {message.meta?.processingTime && <span>Time: {message.meta.processingTime}</span>}
                  </div>
                )}
            </div>
            <div className={styles.topMetaBar} style={{ marginTop: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {!isFormMessage && (
                  <Tooltip content={copiedMessageId === message.id ? "Copied!" : "Copy"} relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      className={styles.copyButton}
                      icon={copiedMessageId === message.id ? <CheckmarkRegular /> : <CopyRegular />}
                      onClick={() => handleCopyMessage(message)}
                      aria-label="Copy"                    
                      tabIndex={0}
                    />
                  </Tooltip>
                )}
                {COPILOT_CONSTANTS.FEATURES.SHOW_EXIT_TOGGLE && message.role === 'assistant' && formSessionActive && (
                  <Tooltip content="Exit from current mode to create ticket" relationship="label">
                    <div className={styles.draftSwitchContainer}>
                      <Switch 
                        label="Exit"
                        defaultChecked={false}
                        disabled={shouldDisableSwitch(message.id)}
                        onChange={shouldDisableSwitch(message.id) ? undefined : handleExitToggle}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                {message.role === 'assistant' && (
                  <AiGeneratedDisclaimer>
                    {COPILOT_CONSTANTS.MESSAGES.DISCLAIMER}
                  </AiGeneratedDisclaimer>
                )}
                {process.env.NODE_ENV === 'development' && tsLabel && <span className={styles.timestampText}>{tsLabel}</span>}
              </div>
            </div>
          </CopilotMessage>
        );
      })}
      
      {isLoading && (
        <div className={styles.loadingWrapper}>
          <Spinner size="tiny" aria-label="Loading response from MCAPSHelp Copilot" />
        </div>
      )}
      
      {/* Removed local aria-live region - using global one from SimpleCopilotPanel */}
    </>
  );
};


export default React.memo(MessageBody);

// Generated by Copilot
// updated on: 2026-01-01 | 30+ lines added/updated | Feature Integration | Integrated Adaptive Card rendering with feature flag support and conditional rendering logic | Component Enhancement
